import { FormArray, FormGroup } from '@angular/forms';
import type {
  FieldConditionDto,
  TemplateVariable,
} from '@app/+authenticated/+employees/templates/contract-template/models/contract-template.model';
import type { HiringFileResource } from '@app/+new-hire/sectioned-form/hiring-field/hiring-file-upload/hiring-file-resource.model';

export interface AutofillConfig {
  form: FormGroup;
  questions: TemplateVariable[];
  // Uploads a generated file and resolves its resource id (real upload, so the
  // filled form is actually submittable).
  uploadFile: (file: File) => Promise<string>;
  // Delegates row creation to the form component (it owns the FormArray shape).
  addChildRow: (field: TemplateVariable) => void;
}

// Fills the new-hire form for testing. Files are uploaded first (real uploads,
// so the form is submittable), then everything is applied in a single patch so
// conditional fields reveal and the uploaded files render together. Children
// rows are added last, since the FormArray only exists after the patch.
export async function autofillHiringForm(config: AutofillConfig): Promise<void> {
  // A per-run token keeps every generated email unique so each autofilled hire
  // gets a fresh address (the backend rejects a duplicate hire email).
  const runId = Math.random().toString(36).slice(2, 8);
  const fileValues = await uploadFileValues(config);
  config.form.patchValue({ ...buildScalarValues(config.questions, runId), ...fileValues });
  fillChildrenFields(config, runId);
}

// ── scalars ─────────────────────────────────────────────────────────────────

// One value per non-file, non-children field. Trigger fields referenced by a
// `condition` are set to a value that satisfies it, so dependents reveal + fill.
function buildScalarValues(questions: TemplateVariable[], runId: string): Record<string, unknown> {
  const values: Record<string, unknown> = {};

  for (const field of questions) {
    if (field.internal || isFileField(field) || field.type === 'children') continue;
    values[field.variable] = scalarValueFor(field, runId);
  }

  for (const field of questions) {
    if (field.condition) applyRevealValue(field.condition, values);
  }

  return values;
}

function scalarValueFor(field: TemplateVariable, runId: string): unknown {
  switch (field.type) {
    case 'boolean':
      return field.mandatory; // requiredTrue fields get `true`, the rest `false`
    case 'select':
      return field.options?.items?.[0]?.value ?? '';
    case 'multi-select': {
      const firstOption = field.options?.items?.[0]?.value;
      return firstOption ? [firstOption] : [];
    }
    case 'date':
      return isoBirthDateForAge(30);
    case 'email':
      // `+runId` keeps it unique per run; the field-name prefix keeps the hire
      // and guardian addresses distinct (the guardian-email-must-differ validator).
      return `${field.variable.replace(/[^a-zA-Z0-9._-]/g, '') || 'dev'}+${runId}@example.com`;
    case 'phone':
      return '+4915123456789';
    case 'number':
    case 'decimal':
    case 'money':
      return '10';
    default:
      return textValueFor(field);
  }
}

function textValueFor(field: TemplateVariable): string {
  const value = keywordValueFor(field);
  const minLength = field.options?.minlength ?? 0;
  const maxLength = field.options?.maxlength ?? Number.POSITIVE_INFINITY;

  if (value.length > maxLength) return value.slice(0, maxLength);
  if (value.length < minLength) return value.padEnd(minLength, 'x');
  return value;
}

function keywordValueFor(field: TemplateVariable): string {
  const haystack = `${field.label} ${field.variable}`.toLowerCase();
  if (haystack.includes('iban')) return 'DE89370400440532013000';
  if (haystack.includes('bic')) return 'COBADEFFXXX';
  if (haystack.includes('steuer') || haystack.includes('tax identification')) return '12345678901';
  if (haystack.includes('sozial') || haystack.includes('social security')) return '123456789012';
  if (haystack.includes('postal') || haystack.includes('postcode') || haystack.includes('zip')) return '80802';
  if (haystack.includes('house')) return '24';
  if (haystack.includes('first')) return 'Lena';
  if (haystack.includes('last') || haystack.includes('surname')) return 'Fischer';
  if (haystack.includes('city')) return 'Munich';
  if (haystack.includes('street') || haystack.includes('address')) return 'Leopoldstrasse';
  if (haystack.includes('place of birth') || haystack.includes('place_of_birth')) return 'Munich';
  return 'Autofill';
}

// ── children ────────────────────────────────────────────────────────────────

function fillChildrenFields(config: AutofillConfig, runId: string): void {
  for (const field of config.questions) {
    if (field.type !== 'children') continue;

    config.addChildRow(field);

    const rows = config.form.get(field.variable);
    if (!(rows instanceof FormArray)) continue;

    const rowValues = buildChildRowValues(field.options?.fields ?? [], runId);
    for (const row of rows.controls) {
      if (row instanceof FormGroup) row.patchValue(rowValues);
    }
  }
}

function buildChildRowValues(
  subFields: NonNullable<NonNullable<TemplateVariable['options']>['fields']>,
  runId: string,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const subField of subFields) {
    if (subField.type === 'file' || subField.type === 'file_collection' || subField.type === 'children') continue;
    values[subField.variable] = scalarValueFor(
      {
        variable: subField.variable,
        type: subField.type,
        label: subField.label,
        mandatory: subField.mandatory ?? false,
        internal: false,
        category: { key: 'children', label: 'Children' },
      },
      runId,
    );
  }
  return values;
}

// ── files ───────────────────────────────────────────────────────────────────

// Uploads one generated file per file field and returns a { variable: value }
// map (a single resource for `file`, an array for `file_collection`) to fold
// into the form patch.
async function uploadFileValues(config: AutofillConfig): Promise<Record<string, unknown>> {
  const fileFields = config.questions.filter(isFileField);

  const values: Record<string, unknown> = {};

  await Promise.all(
    fileFields.map(async (field) => {
      const file = generatedPdf();
      const resourceId = await config.uploadFile(file);
      const resource: HiringFileResource = { id: resourceId, name: file.name, size: file.size };
      values[field.variable] = field.type === 'file_collection' ? [resource] : resource;
    }),
  );

  return values;
}

function generatedPdf(): File {
  const pdf = '%PDF-1.4\n1 0 obj<<>>endobj\ntrailer<<>>\n%%EOF\n';
  return new File([pdf], 'autofill.pdf', { type: 'application/pdf' });
}

// ── conditions ──────────────────────────────────────────────────────────────

function applyRevealValue(condition: FieldConditionDto, values: Record<string, unknown>): void {
  switch (condition.type) {
    case 'all_of':
      for (const nested of condition.conditions) applyRevealValue(nested, values);
      return;
    case 'answer_is_one_of':
      values[condition.field] = condition.values[0];
      return;
    case 'answer_is_not_one_of':
      values[condition.field] = pickValueNotIn(condition.values);
      return;
    case 'age_under':
      values[condition.field] = isoBirthDateForAge(Math.max(condition.years - 2, 0));
      return;
    case 'age_at_least':
      values[condition.field] = isoBirthDateForAge(condition.years + 1);
      return;
  }
}

function pickValueNotIn(exclusions: string[]): string {
  const candidates = ['BR', 'US', 'TR', 'JP', 'CN', 'ZA'];
  return candidates.find((candidate) => !exclusions.includes(candidate)) ?? 'BR';
}

function isFileField(field: TemplateVariable): boolean {
  return field.type === 'file' || field.type === 'file_collection';
}

function isoBirthDateForAge(age: number): string {
  const today = new Date();
  const birthDate = new Date(today.getFullYear() - age, today.getMonth(), today.getDate());
  const month = `${birthDate.getMonth() + 1}`.padStart(2, '0');
  const day = `${birthDate.getDate()}`.padStart(2, '0');
  return `${birthDate.getFullYear()}-${month}-${day}`;
}
